/**
 * Connects to LiveKit and renders the composed layer output (playState.layers).
 * Used for OUTPUT Views in the Perform panel — mirrors what the player displays.
 */

import { useEffect, useRef } from 'react';
import { Box, CircularProgress, Paper, Typography } from '@mui/material';
import { useLivekitTokenMutation } from '@/state/mutations/player/use-livekit-token';
import { useHasSharedLiveKitRoom } from './shared-livekit-room.context';
import { OutputLayerPreview } from './output-layer-preview';
import type { OutputLayer } from './output-layer-preview';
import type { PlayerEventSettings } from '@/types/preview.types';

import type { PresentationSlideNavigationScope } from '@/components/preview/preview-presentation-slides-layer.types';
import type { LiveContextStateDto } from '@/state/queries/player/use-get-live-context-state';

export interface LiveKitOutputConnectorProps {
  /** LiveKit room ID for connection (entityType:entityId:onAir) */
  livekitRoomId: string;
  layers: OutputLayer[];
  settings?: PlayerEventSettings | null;
  participantIdentity?: string;
  height?: string | number;
  onError?: (error: Error) => void;
  slideNavigationScope?: PresentationSlideNavigationScope;
  /** Next look-ahead: step stills locally instead of waiting for the presentation computer. */
  preferAbsoluteSlideGoto?: boolean;
  sequencePlaybackControls?: boolean;
  showAgentDeckSlideOverlay?: boolean;
  liveContextState?: LiveContextStateDto;
  entityRef?: string;
  /** Freeze non-LiveKit video file layers on the first frame (Control Now/Next). */
  stillFrame?: boolean;
  /** Live Current: file videos play at the players' position (layer `playbackStartedAt`). */
  syncFileVideo?: boolean;
  /** False while connecting; then follows still-frame content readiness when enabled. */
  onReadyChange?: (ready: boolean) => void;
  stillFrameResetNonce?: number;
  previewAudioEnabled?: boolean;
  onPreviewAudioEnabledChange?: (enabled: boolean) => void;
  showInFrameAudioToggle?: boolean;
}

/**
 * Fetches LiveKit token and renders the composed layer output.
 * Requires playState.layers from room state — show placeholder when no layers.
 */
export function LiveKitOutputConnector({
  livekitRoomId,
  layers,
  settings,
  participantIdentity,
  height = '100%',
  onError,
  slideNavigationScope,
  preferAbsoluteSlideGoto,
  sequencePlaybackControls,
  showAgentDeckSlideOverlay,
  liveContextState,
  entityRef,
  stillFrame = false,
  syncFileVideo = false,
  onReadyChange,
  stillFrameResetNonce = 0,
  previewAudioEnabled,
  onPreviewAudioEnabledChange,
  showInFrameAudioToggle,
}: LiveKitOutputConnectorProps) {
  const hasSharedRoom = useHasSharedLiveKitRoom();
  const identityRef = useRef(participantIdentity ?? `output-preview-${Date.now()}`);
  const identity = identityRef.current;
  const mutation = useLivekitTokenMutation();

  useEffect(() => {
    if (livekitRoomId && !hasSharedRoom) {
      mutation.mutate({
        livekitRoomId,
        participantIdentity: identity,
        mode: 'preview',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only fetch when livekitRoomId changes
  }, [livekitRoomId, hasSharedRoom]);

  const onReadyChangeRef = useRef(onReadyChange);
  useEffect(() => {
    onReadyChangeRef.current = onReadyChange;
  }, [onReadyChange]);

  useEffect(() => {
    if (!hasSharedRoom && !mutation.data && mutation.isPending) {
      onReadyChangeRef.current?.(false);
    }
  }, [hasSharedRoom, mutation.isPending, mutation.data]);

  // File stills report through the still-frame provider; otherwise readiness is the room connection.
  const onRoomConnectedChange = stillFrame ? undefined : onReadyChange;

  if (hasSharedRoom) {
    return (
      <OutputLayerPreview
        layers={layers}
        settings={settings}
        onError={onError}
        height={height}
        slideNavigationScope={slideNavigationScope}
        preferAbsoluteSlideGoto={preferAbsoluteSlideGoto}
        sequencePlaybackControls={sequencePlaybackControls}
        showAgentDeckSlideOverlay={showAgentDeckSlideOverlay}
        liveContextState={liveContextState}
        entityRef={entityRef}
        stillFrame={stillFrame}
        syncFileVideo={syncFileVideo}
        onStillFrameReadyChange={stillFrame ? onReadyChange : undefined}
        onRoomConnectedChange={onRoomConnectedChange}
        stillFrameResetNonce={stillFrameResetNonce}
        previewAudioEnabled={previewAudioEnabled}
        onPreviewAudioEnabledChange={onPreviewAudioEnabledChange}
        showInFrameAudioToggle={showInFrameAudioToggle}
      />
    );
  }

  if (mutation.isError) {
    return (
      <Paper
        elevation={2}
        sx={{
          height,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          p: 4,
          bgcolor: '#f5f5f5',
        }}
      >
        <Typography variant="h6" color="error" gutterBottom>
          Failed to connect
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center">
          {mutation.error instanceof Error ? mutation.error.message : 'Unknown error'}
        </Typography>
      </Paper>
    );
  }

  if (mutation.isPending || !mutation.data) {
    return (
      <Box
        sx={{
          height,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          bgcolor: '#000',
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          Connecting to LiveKit...
        </Typography>
      </Box>
    );
  }

  const { token, host } = mutation.data;

  return (
    <OutputLayerPreview
      serverUrl={host}
      token={token}
      layers={layers}
      settings={settings}
      onError={onError}
      height={height}
      slideNavigationScope={slideNavigationScope}
      preferAbsoluteSlideGoto={preferAbsoluteSlideGoto}
      sequencePlaybackControls={sequencePlaybackControls}
      showAgentDeckSlideOverlay={showAgentDeckSlideOverlay}
      liveContextState={liveContextState}
      entityRef={entityRef}
      stillFrame={stillFrame}
      syncFileVideo={syncFileVideo}
      onStillFrameReadyChange={stillFrame ? onReadyChange : undefined}
      onRoomConnectedChange={onRoomConnectedChange}
      stillFrameResetNonce={stillFrameResetNonce}
      previewAudioEnabled={previewAudioEnabled}
      onPreviewAudioEnabledChange={onPreviewAudioEnabledChange}
      showInFrameAudioToggle={showInFrameAudioToggle}
    />
  );
}
